import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import { initiatePaystackPayment } from '../services/payments';

const PRICING_TIERS = [
  { id: 'small', label: 'Small Deal (< R500k)', price: 8500, deliveryDays: 2 },
  { id: 'medium', label: 'Medium Deal (R500k - R5M)', price: 12500, deliveryDays: 2 },
  { id: 'large', label: 'Large Deal (> R5M)', price: 18500, deliveryDays: 3 },
];

export default function Audit() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: '',
    company: '',
    dealType: '',
    dealValue: '',
    notes: '',
    tier: 'small',
  });
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedTier = PRICING_TIERS.find(t => t.id === formData.tier)!;

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setFile(selectedFile);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!file) {
      setError('Please upload a contract document');
      return;
    }

    if (!user) {
      setError('Please sign in to submit an audit request');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload file to Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${formData.company.replace(/\s/g, '_')}.${fileExt}`;
      const storageRef = ref(storage, `audit_uploads/${user.uid}/${fileName}`);

      const uploadTask = uploadBytesResumable(storageRef, file);

      // Monitor upload progress
      await new Promise<string>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });

      // Create audit request in Firestore
      const auditRequest = {
        userId: user.uid,
        email: formData.email.toLowerCase().trim(),
        name: formData.name.trim(),
        company: formData.company.trim(),
        dealType: formData.dealType.trim(),
        dealValue: formData.dealValue.trim(),
        notes: formData.notes.trim(),
        tier: formData.tier,
        priceZAR: selectedTier.price,
        fileName: file.name,
        fileUrl: `audit_uploads/${user.uid}/${fileName}`,
        status: 'pending_payment',
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'audit_requests'), auditRequest);

      // Initiate payment
      await initiatePaystackPayment({
        email: formData.email.toLowerCase().trim(),
        amount: selectedTier.price * 100, // Convert to cents
        metadata: {
          productId: docRef.id,
          auditRequestId: docRef.id,
          productType: 'audit',
          tier: formData.tier,
        },
      });

      // On success, redirect to thank you page
      window.location.href = '/thank-you?type=audit';
    } catch (err: any) {
      console.error('Audit submission error:', err);
      setError(err.message || 'Failed to submit audit request');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-4xl px-4">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-primary mb-4">
            48-Hour Contract Audit
          </h1>
          <p className="text-xl text-secondary max-w-2xl mx-auto">
            Get expert legal review of your contract before signing.
            Protect your business from hidden risks and unfavorable terms.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {PRICING_TIERS.map(tier => (
            <div
              key={tier.id}
              onClick={() => setFormData(prev => ({ ...prev, tier: tier.id }))}
              className={`cursor-pointer border-2 rounded-lg p-6 transition-all ${
                formData.tier === tier.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-400'
              }`}
            >
              <h3 className="font-semibold text-lg mb-2">{tier.label}</h3>
              <p className="text-3xl font-bold text-primary mb-2">
                R{tier.price.toLocaleString()}
              </p>
              <p className="text-sm text-secondary">
                {tier.deliveryDays} business day turnaround
              </p>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-semibold mb-6">What You Get</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="text-blue-600 mr-3 text-xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Comprehensive Review</h3>
                <p className="text-sm text-secondary">
                  Line-by-line analysis of all clauses and terms
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="text-blue-600 mr-3 text-xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Risk Assessment</h3>
                <p className="text-sm text-secondary">
                  Identify potential liabilities and unfavorable terms
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="text-blue-600 mr-3 text-xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Redline Suggestions</h3>
                <p className="text-sm text-secondary">
                  Specific changes to protect your interests
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="text-blue-600 mr-3 text-xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Executive Summary</h3>
                <p className="text-sm text-secondary">
                  Clear recommendations in plain English
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Request Form */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold mb-6">Request Your Audit</h2>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deal Type *
                </label>
                <input
                  type="text"
                  name="dealType"
                  value={formData.dealType}
                  onChange={handleInputChange}
                  placeholder="e.g., Service Agreement, Supply Contract"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deal Value (ZAR) *
              </label>
              <input
                type="text"
                name="dealValue"
                value={formData.dealValue}
                onChange={handleInputChange}
                placeholder="e.g., R250,000"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Contract *
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-secondary mt-1">
                PDF or Word document, max 10MB
              </p>
              {file && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                placeholder="Any specific concerns or areas you'd like us to focus on?"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h3 className="font-semibold mb-2">Order Summary</h3>
              <p className="text-sm text-secondary mb-2">
                {selectedTier.label}
              </p>
              <p className="text-2xl font-bold text-primary">
                R{selectedTier.price.toLocaleString()}
              </p>
              <p className="text-sm text-secondary mt-2">
                Delivery: {selectedTier.deliveryDays} business days
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-md font-medium text-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            >
              {loading ? 'Processing...' : `Pay R${selectedTier.price.toLocaleString()} & Submit`}
            </button>

            <p className="text-xs text-center text-secondary">
              By submitting, you agree to our{' '}
              <a href="/terms" className="text-blue-600 hover:underline">
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </a>
            </p>
          </form>
        </div>

        {/* FAQ */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">How long does the audit take?</h3>
              <p className="text-sm text-secondary">
                We deliver your comprehensive audit report within 2-3 business days, depending on the deal size.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">What if I need urgent review?</h3>
              <p className="text-sm text-secondary">
                Contact us at support@dealvelocity.co.za for rush service (additional fees apply).
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Is my contract confidential?</h3>
              <p className="text-sm text-secondary">
                Absolutely. All documents are stored securely and covered by attorney-client privilege.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
